export type CompanyKind = 'internal' | 'external';
export type CompanyStatus = 'active' | 'prospect' | 'dormant' | 'archived';
export type ProjectStatus = 'discovery' | 'active' | 'on_hold' | 'delivered' | 'archived';
export type CompanyRole = 'user' | 'client_admin' | 'client';
export type Priority = 'critical' | 'high' | 'medium' | 'low' | 'none';
export type StatusCategory = 'backlog' | 'todo' | 'active' | 'blocked' | 'review' | 'done';
export type Theme = 'dark' | 'light' | 'system';
export type Grant = 'full' | 'own' | 'none';

/** Where a task has got to inside "in progress". Null means nobody has said. */
export type WorkStage = 'research' | 'design' | 'developing' | 'developed' | 'testing' | 'done';
export const WORK_STAGES: WorkStage[] = ['research', 'design', 'developing', 'developed', 'testing', 'done'];
export const WORK_STAGE_LABELS: Record<WorkStage, string> = {
  research: 'Research',
  /** Worked out, not yet built: the shape of the thing, agreed before anybody writes it. */
  design: 'Design',
  developing: 'Developing',
  /** Built and handed over. Setting it moves the card to In review. */
  developed: 'Development done',
  testing: 'Testing',
  /** Tested and through. Setting it moves the card to Done, so the two never disagree. */
  done: 'Done',
};

export const ROLE_LABELS: Record<CompanyRole, string> = {
  user: 'User',
  client_admin: 'Client admin',
  client: 'Client',
};

export const ROLE_HINTS: Record<CompanyRole, string> = {
  user: 'A developer. Gets assigned tasks, updates them, merges, logs time.',
  client_admin:
    "The customer's lead. Changes their company's work, and adds their own people — who arrive as clients. Only we can appoint one.",
  client:
    "The customer's people. See everything of theirs and the reporting; raise a request and edit what is theirs.",
};

/**
 * The statuses that mean a project is behind us.
 *
 * `delivered` is finished and `archived` is finished and put away — the difference matters to
 * somebody reading one, and not at all to a list deciding whether to show it. Named once here
 * because two pages ask the same question and a second copy is how they stop agreeing.
 */
export const FINISHED_PROJECT_STATUSES = new Set<ProjectStatus>(['delivered', 'archived']);

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  discovery: 'Discovery',
  active: 'Active',
  on_hold: 'On hold',
  delivered: 'Delivered',
  archived: 'Archived',
};

export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  active: 'Active',
  prospect: 'Prospect',
  dormant: 'Dormant',
  archived: 'Archived',
};

export interface Session {
  person: {
    id: string;
    name: string;
    email: string;
    jobTitle: string;
    isAdmin: boolean;
    theme: Theme;
    hasGitlabToken: boolean;
    /** Whether a second step is asked for at sign-in, and which one. */
    twoFactorMode: 'off' | 'email' | 'app';
    lastLoginAt: string | null;
    /** The company to reopen on, already checked against what they may see. Null if none. */
    lastCompanyId: string | null;
  };
  companies: Array<{
    id: string;
    name: string;
    code: string;
    kind: CompanyKind;
    /**
     * NULL FOR A COMPANY SOMEBODY REACHES WITHOUT BELONGING TO IT — today, one the billing hat
     * can invoice. The capabilities beside it are then whatever the hat grants over no role at
     * all, which is how reach stays separate from power.
     */
    role: CompanyRole | null;
    capabilities: Record<string, Grant>;
  }>;
  platformCapabilities: Record<string, Grant>;
  /** How many active administrators exist — what makes the sole-admin lockout warning precise. */
  activeAdminCount: number;
  /**
   * When this session hard-ends: twelve hours from signing in, never extended by activity. The
   * app signs itself out at this moment rather than waiting for a click to fail.
   */
  expiresAt: string | null;
  /** The server's clock when it answered — what `expiresAt` is counted down from. */
  serverNow: string | null;
}

export interface CompanySummary {
  id: string;
  name: string;
  code: string;
  kind: CompanyKind;
  status: CompanyStatus;
  notes: string;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  role: CompanyRole | null;
  projectCount: number;
  activeProjectCount: number;
  createdAt: string;
}

export interface Project {
  id: string;
  companyId: string;
  name: string;
  code: string;
  description: string;
  status: ProjectStatus;
  priority: Priority;
  startDate: string | null;
  targetDate: string | null;
  /**
   * The agreed amount, in rand — PRESENT only for whoever may see money (absent, not null,
   * for everybody else). Null when none is set.
   */
  budget?: number | null;
  baselineTargetDate: string | null;
  gitlabProjectPath: string;
  gitlabDefaultBranch: string;
  billable: boolean;
  restricted: boolean;
  /** Whether the customer's own people see this project at all. */
  sharedWithClient: boolean;
  slippageDays?: number | null;
  overdue?: boolean;
}

export interface WorkflowStatus {
  id: string;
  label: string;
  color: string;
  position: number;
  category: StatusCategory;
  gitlabLabel: string;
  isDefault: boolean;
}

export interface PriorityItem {
  id: string;
  key: string;
  title: string;
  type: string;
  priority: Priority;
  severity: string;
  dueDate: string | null;
  overdue: boolean;
  blockedSince: string | null;
  status: { id: string; label: string; color: string; category: StatusCategory } | null;
  assignees: Array<{ id: string; name: string }>;
  project: { id: string; name: string; code: string; priority: Priority };
}

export interface PersonRow {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  isAdmin: boolean;
  /**
   * The testing hat, added over whatever role they hold — not a role of its own.
   *
   * A flag rather than a company role so that making somebody a tester does not take away
   * their `client`, and with it their own client admin's ability to manage them.
   */
  isTester: boolean;
  /** Handles the billing. A hat like `isTester`, not a company role. */
  isFinance: boolean;
  isActive: boolean;
  /** Whether a second step is asked for at their sign-in, so it can be cleared if they are stuck. */
  twoFactorMode: 'off' | 'email' | 'app';
  lastLoginAt: string | null;
  companies: Array<{ id: string; name: string; code: string; role: CompanyRole }>;
}

export type WorkItemType = 'task' | 'bug' | 'feature' | 'improvement' | 'chore' | 'spike';
export type Severity = 'blocker' | 'critical' | 'major' | 'minor' | 'trivial' | 'none';

export const WORK_ITEM_TYPE_LABELS: Record<WorkItemType, string> = {
  task: 'Task',
  bug: 'Bug',
  feature: 'Feature',
  improvement: 'Improvement',
  chore: 'Chore',
  spike: 'Spike',
};

/** A short glyph per type, so a card's nature reads at a glance on a dense board. */
export const WORK_ITEM_TYPE_GLYPH: Record<WorkItemType, string> = {
  task: '◻',
  bug: '⬤',
  feature: '✦',
  improvement: '↑',
  chore: '⚙',
  spike: '⌁',
};

export interface WorkItemCard {
  id: string;
  key: string;
  projectId: string;
  /** Set by us, read by everybody — see the `work.stage` capability. */
  stage: WorkStage | null;
  /**
   * How far along, 0-100. THE number — worked out once on the server by `taskPercent` so the
   * table, the kanban, the timeline, the diagram and the report all show the same thing.
   *
   * Null means nobody has said anything yet, which is not the same as 0.
   */
  percentComplete: number | null;
  groupId: string | null;
  category: { id: string; name: string; path: string } | null;
  statusId: string | null;
  impact: Impact | null;
  urgency: Urgency | null;
  sla: SlaView | null;
  title: string;
  /** Whether time on it is charged: true, false, or null for "as the project is". Ours to set. */
  billable?: boolean | null;
  /** Set only when the task is charged unlike its project. */
  billingException?: BillingException;
  description: string;
  reproSteps: string;
  type: WorkItemType;
  priority: Priority;
  severity: Severity;
  points: number | null;
  estimateMinutes: number | null;
  startDate: string | null;
  dueDate: string | null;
  overdue: boolean;
  position: number;
  blockedSince: string | null;
  blockedReason: string;
  startedAt: string | null;
  completedAt: string | null;
  resolution: string;
  closedAt: string | null;
  reopenCount: number;
  parentId: string | null;
  gitlabIssueIid: number | null;
  merge: MergeSnapshot | null;
  assignees: Array<{ id: string; name: string }>;
  reporter: { id: string; name: string } | null;
  createdAt: string;
  editable: boolean;
}

export interface BoardColumn {
  id: string;
  label: string;
  color: string;
  category: StatusCategory;
  gitlabLabel: string;
  items: WorkItemCard[];
}

export interface TaskGroup {
  id: string;
  name: string;
  color: string;
  position: number;
  collapsed: boolean;
  items: WorkItemCard[];
}

export interface Board {
  project: { id: string; name: string; code: string; companyId: string };
  role: CompanyRole | null;
  capabilities: Record<string, Grant>;
  transitionsEnforced: boolean;
  /** Status columns — the kanban reading. */
  columns: BoardColumn[];
  /** Sections — the table reading. Same cards, other axis. */
  groups: TaskGroup[];
  ungrouped: WorkItemCard[];
  unassigned: WorkItemCard[];
  /** Closed work is off the board but never gone; this says whether any is waiting there. */
  closedCount: number;
  includingClosed: boolean;
}

/**
 * A project on the Project management page: enough to choose between them at a glance.
 *
 * The counts are real numbers, zero included — a project with nothing open says 0 rather than
 * leaving the card blank, because blank reads as "not loaded".
 */
export interface ProjectCard {
  id: string;
  name: string;
  code: string;
  status: ProjectStatus;
  priority: Priority;
  targetDate: string | null;
  restricted: boolean;
  sharedWithClient: boolean;
  gitlabProjectPath: string;
  company: { id: string; name: string; code: string; kind: CompanyKind };
  open: number;
  overdue: number;
  blocked: number;
}

export interface AssignablePerson {
  id: string;
  name: string;
  email: string;
  role: CompanyRole;
}

/** What the board knows about a task's merge request without calling GitLab. */
export interface MergeSnapshot {
  iid: number;
  title: string;
  state: string;
  pipelineStatus: string;
  sourceBranch: string;
  targetBranch: string;
  webUrl: string;
  syncedAt: string | null;
}

export interface GitlabStatus {
  linked: boolean;
  reason?: string;
  host: string;
  projectPath?: string;
  repository?: { name: string; webUrl: string; defaultBranch: string };
  canMerge: boolean;
  usingOwnToken: boolean;
}

export interface GitlabMergeOption {
  iid: number;
  title: string;
  state: string;
  sourceBranch: string;
  targetBranch: string;
  author: string;
  webUrl: string;
  updatedAt: string;
  draft: boolean;
  linkedTo: string | null;
}

export interface GitlabDetail {
  linked: boolean;
  canMerge: boolean;
  mergeRequest?: {
    iid: number;
    title: string;
    state: string;
    webUrl: string;
    sourceBranch: string;
    targetBranch: string;
    author: string;
    draft: boolean;
    hasConflicts: boolean;
    mergeStatus: string;
    changesCount: string | null;
    commentCount: number;
    mergedAt: string | null;
    updatedAt: string;
  };
  pipeline?: { id: number; status: string; webUrl: string; ref: string } | null;
  approvals?: { approved: boolean; required: number | null; left: number | null; approvedBy: string[] } | null;
  mergeBlockers?: string[];
}

/** Pipeline and merge states map onto the theme's three signal colours. */
export function pipelineTone(status: string): 'ok' | 'warn' | 'bad' | 'muted' {
  if (status === 'success') return 'ok';
  if (status === 'failed' || status === 'canceled') return 'bad';
  if (status === 'running' || status === 'pending' || status === 'created') return 'warn';
  return 'muted';
}

export type Forecast = 'on_track' | 'at_risk' | 'late' | 'done' | 'unknown';

export const FORECAST_LABELS: Record<Forecast, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  late: 'Late',
  done: 'Delivered',
  unknown: 'No date',
};

export interface ProjectReport {
  id: string;
  name: string;
  code: string;
  status: ProjectStatus;
  priority: Priority;
  startDate: string | null;
  targetDate: string | null;
  baselineTargetDate: string | null;
  slippageDays: number | null;
  items: { total: number; done: number; open: number; inProgress: number; blocked: number; overdue: number };
  points: { total: number; done: number } | null;
  percentComplete: number;
  /**
   * Where the OPEN work has got to, plus what is finished.
   *
   * "60% complete" says how much is done and nothing about the rest; this says what the rest
   * is doing. `unsaid` is its own number rather than folded into research — a task nobody has
   * staged is not in research, and saying so would be inventing progress.
   */
  stages: {
    research: number;
    design: number;
    developing: number;
    developed: number;
    testing: number;
    unsaid: number;
    done: number;
  };
  /** Through testing and out, as a share of everything. */
  percentPassed: number;
  throughput: { completedInWindow: number; perDay: number; perWeek: number };
  projectedFinish: string | null;
  projectionBasis: string;
  projectionConfidence: 'good' | 'fair' | 'low' | 'none' | 'n/a';
  lastCompletedAt: string | null;
  forecast: Forecast;
}

export interface CompanyReport {
  company: { id: string; name: string; code: string; kind: CompanyKind };
  generatedAt: string;
  windowDays: number;
  totals: {
    projects: number;
    items: number;
    done: number;
    open: number;
    blocked: number;
    overdue: number;
    completedInWindow: number;
    percentComplete: number;
    perWeek: number;
  };
  forecast: { late: number; atRisk: number; onTrack: number; unknown: number };
  projects: ProjectReport[];
}

export interface PortfolioRow {
  id: string;
  name: string;
  code: string;
  kind: CompanyKind;
  projects: number;
  items: number;
  done: number;
  open: number;
  blocked: number;
  overdue: number;
  percentComplete: number;
  perWeek: number;
  forecast: { late: number; atRisk: number; onTrack: number; unknown: number };
  nextTarget: string | null;
}

export type TimeStatus = 'draft' | 'submitted' | 'approved' | 'rejected';

export const TIME_STATUS_LABELS: Record<TimeStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export interface TimeEntryRow {
  id: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  workItem: { id: string; key: string; title: string } | null;
  workDate: string;
  minutes: number;
  description: string;
  billable: boolean;
  status: TimeStatus;
  rejectionReason: string;
  running: boolean;
  startedAt: string | null;
  editable: boolean;
}

export interface TimeWeek {
  personId: string;
  from: string;
  to: string;
  totalMinutes: number;
  billableMinutes: number;
  submittableIds: string[];
  byStatus: Partial<Record<TimeStatus, number>>;
  days: Array<{ date: string; minutes: number; entries: TimeEntryRow[] }>;
}

export interface RunningTimer {
  id: string;
  projectId: string;
  projectName: string;
  workItem: { id: string; key: string; title: string } | null;
  description: string;
  billable: boolean;
  startedAt: string;
  elapsedMinutes: number;
}

export interface PendingTime {
  totalEntries: number;
  totalMinutes: number;
  people: Array<{ personId: string; name: string; minutes: number; entries: TimeEntryRow[] }>;
}

/** 195 reads as "3h 15m"; a bare minute count is no use on a timesheet. */
export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/** Monday-based, because that is how the week is worked. */
export function startOfWeek(date: Date): string {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return toDateString(d);
}

export function toDateString(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return toDateString(d);
}

export interface WorkItemTime {
  totalMinutes: number;
  estimateMinutes: number | null;
  entries: Array<{
    id: string;
    person: string;
    workDate: string;
    minutes: number;
    description: string;
    billable: boolean;
    status: TimeStatus;
  }>;
}

export interface CompanyDashboard {
  company: { id: string; name: string; code: string; kind: CompanyKind };
  role: CompanyRole | null;
  capabilities: Record<string, Grant>;
  projects: Project[];
  totals: {
    tasks: number;
    done: number;
    open: number;
    inProgress: number;
    blocked: number;
    overdue: number;
    unassigned: number;
  };
  byStatus: Array<{ label: string; color: string; category: StatusCategory; count: number }>;
  byPriority: Array<{ priority: Priority; count: number }>;
  /** What the open work is about, most common first. */
  byCategory: Array<{ label: string; count: number }>;
  /** Null for a client: their company's workload per person is not their view. */
  byAssignee: Array<{ id: string; name: string; count: number }> | null;
  recent: Array<{ action: string; label: string; actor: string; at: string }>;
}

/** `work_item.moved` reads as "moved work item" on a dashboard. */
export function describeAction(action: string): string {
  const [entity, verb] = action.split('.');
  const nouns: Record<string, string> = {
    work_item: 'task',
    company: 'company',
    project: 'project',
    person: 'account',
    time_entry: 'time',
    gitlab: 'GitLab',
    auth: 'session',
  };
  const verbs: Record<string, string> = {
    created: 'created',
    updated: 'updated',
    moved: 'moved',
    deleted: 'deleted',
    merged: 'merged',
    approved: 'approved',
    rejected: 'rejected',
    submitted: 'submitted',
    login: 'signed in',
    logout: 'signed out',
    role_granted: 'granted access to',
    role_revoked: 'revoked access to',
    password_reset: 'reset the password for',
    password_changed: 'changed password',
    team_member_set: 'added to the team of',
    team_member_removed: 'removed from the team of',
    merge_request_linked: 'linked a merge request to',
    merge_request_unlinked: 'unlinked a merge request from',
    merge_request_approved: 'approved the merge request on',
    timer_stopped: 'booked time',
  };
  return `${verbs[verb] ?? verb} ${nouns[entity] ?? entity}`;
}

export interface TaskComment {
  id: string;
  body: string;
  author: { id: string; name: string } | null;
  source: 'hub' | 'gitlab' | 'freshdesk';
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
  /** The author alone — an administrator cannot rewrite what somebody else said. */
  canEdit: boolean;
  /** The author, or an administrator taking down what should not have been posted. */
  canDelete: boolean;
}

/** Halo's two questions. Asked separately so the matrix, not a mood, decides priority. */
export type Impact = 'organisation' | 'department' | 'group' | 'individual';
export type Urgency = 'critical' | 'high' | 'medium' | 'low';

export const IMPACT_LABELS: Record<Impact, string> = {
  organisation: 'The whole company',
  department: 'A department',
  group: 'A few people',
  individual: 'One person',
};

export const URGENCY_LABELS: Record<Urgency, string> = {
  critical: 'Work has stopped',
  high: 'Badly slowed',
  medium: 'Annoying but workable',
  low: 'Can wait',
};

export type SlaStanding = 'breached' | 'at_risk' | 'on_track' | 'met';

export const SLA_STANDING_LABELS: Record<SlaStanding, string> = {
  breached: 'Breached',
  at_risk: 'At risk',
  on_track: 'On track',
  met: 'Met',
};

export interface SlaTarget {
  due: string;
  met: boolean;
  /** Working minutes left; negative once past due. Null once the target has been answered. */
  remainingMinutes: number | null;
  windowMinutes: number;
  breached: boolean;
}

export interface SlaView {
  response: SlaTarget | null;
  resolution: SlaTarget | null;
  paused: boolean;
  pausedSince: string | null;
  pausedMinutes: number;
  standing: SlaStanding;
}

/** "28m", "3h 10m", "-15m" for something already overdue. */
export function formatClock(minutes: number): string {
  const late = minutes < 0;
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  const body = hours === 0 ? `${rest}m` : rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  return late ? `${body} over` : body;
}

/** The company's catalogue, two levels deep. */
export interface CategoryNode {
  id: string;
  name: string;
  shared: boolean;
  active: boolean;
  inUse: number;
  children?: CategoryNode[];
}

export interface HubNotification {
  id: string;
  type: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
  /** Null once the thing it points at has been deleted. */
  target: { id: string; key: string; projectId: string; title: string } | null;
}

export interface NotificationFeed {
  unread: number;
  items: HubNotification[];
}

/** "just now", "12m", "3h", "2d" — a bell has no room for a full timestamp. */
export function shortAgo(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export interface SearchResults {
  query: string;
  tooShort: boolean;
  companies: Array<{ id: string; name: string; code: string; kind: CompanyKind }>;
  projects: Array<{ id: string; name: string; code: string; companyId: string; companyName: string }>;
  tasks: Array<{
    id: string;
    key: string;
    title: string;
    priority: Priority;
    dueDate: string | null;
    done: boolean;
    closed: boolean;
    project: { id: string; name: string; code: string };
    companyName: string;
    status: { label: string; color: string } | null;
    assigneeNames: string | null;
  }>;
  /**
   * The service records. Three separate arrays because they answer to three separate gates —
   * a customer sees their incidents and neither of the other two.
   */
  incidents: Array<{
    id: string;
    reference: string;
    title: string;
    status: IncidentStatus;
    priority: Priority;
    companyId: string;
    companyName: string;
    projectCode: string | null;
  }>;
  problems: Array<{
    id: string;
    reference: string;
    title: string;
    status: ProblemStatus;
    priority: Priority;
    /** Carried so support can read the answer straight off the result. */
    workaround: string;
    incidentCount: number;
    companyId: string;
    companyName: string;
  }>;
  changes: Array<{
    id: string;
    reference: string;
    title: string;
    status: ChangeStatus;
    type: ChangeType;
    risk: RiskLevel;
    mergeIid: number | null;
    project: { id: string; name: string; code: string };
    companyName: string;
  }>;
}

/** One row of somebody's own list of work. */
/** A task charged unlike its project — 'billable' on a free project, 'not_billable' on a charged one. */
export type BillingException = 'billable' | 'not_billable' | null;

export interface MyWorkItem {
  /** Set only when the task is charged unlike its project. */
  billingException?: BillingException;
  id: string;
  key: string;
  title: string;
  type: WorkItemType;
  priority: Priority;
  dueDate: string | null;
  overdue: boolean;
  startedAt: string | null;
  completedAt: string | null;
  closedAt: string | null;
  reopenCount: number;
  blocked: { since: string; reason: string } | null;
  /** Null for internal build work, which carries no clock by design. */
  sla: SlaView | null;
  status: { label: string; color: string; category: StatusCategory } | null;
  project: { id: string; name: string; code: string };
  company: { id: string; name: string };
  assigneeNames: string | null;
  merge: { iid: number; state: string; pipelineStatus: string } | null;
  minutesLogged: number;
  estimateMinutes: number | null;
  bucket: MyWorkBucket;
}

export type MyWorkBucket = 'overdue' | 'today' | 'week' | 'later' | 'undated' | 'waiting' | 'finished';

export interface MyWork {
  person: { id: string; name: string; email: string };
  viewingSelf: boolean;
  /** An administrator may look at somebody else's list; nobody else may. */
  canViewOthers: boolean;
  today: string;
  includingClosed: boolean;
  summary: {
    open: number;
    overdue: number;
    dueToday: number;
    blocked: number;
    waiting: number;
    awaitingClose: number;
    slaBreached: number;
    slaAtRisk: number;
    loggedTodayMinutes: number;
    loggedWeekMinutes: number;
  };
  buckets: Array<{ key: MyWorkBucket; label: string; hint: string; items: MyWorkItem[] }>;
  /** Raised by this person, being done by somebody else. */
  reported: MyWorkItem[];
}

/** Somebody who can be named in a comment on a task. */
export interface MentionablePerson {
  id: string;
  name: string;
  email: string;
  /** `admin` is Lucentra staff, who hold no company role. */
  role: CompanyRole | 'admin';
}

/** A row on the Issues list: a board card plus where it came from. */
export interface IssueRow extends WorkItemCard {
  project: { id: string; name: string; code: string };
  status: { id: string; label: string; color: string; category: StatusCategory } | null;
}

export interface IssueList {
  company: { id: string; name: string; code: string; kind: CompanyKind };
  capabilities: Record<string, Grant>;
  projects: Array<{ id: string; name: string; code: string }>;
  items: IssueRow[];
  total: number;
  /** True when the list hit its ceiling, so the page can say the counts are partial. */
  truncated: boolean;
  closedCount: number;
  includingClosed: boolean;
  withheld: string | null;
}

/** What we would promise for a pair of answers, before anything is raised. */
export interface SlaPreview {
  priority: Priority;
  /** False when the account has no agreed target for that priority: no clock, said plainly. */
  agreed: boolean;
  responseMinutes: number | null;
  resolveMinutes: number | null;
  respondBy: string | null;
  resolveBy: string | null;
  /** Our working week in words — the same for every company. */
  hoursInWords?: string;
}

export type DocumentKind = 'change_request' | 'handover' | 'test_evidence' | 'specification' | 'other';

/** What a document is called on screen. The kinds are named after what actually arrives. */
export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  change_request: 'Change request',
  handover: 'Handover',
  test_evidence: 'Test evidence',
  specification: 'Specification',
  other: 'Document',
};

/** A file kept against a project or one of its tasks. */
export interface ProjectDocument {
  id: string;
  entityType: 'project' | 'work_item';
  entityId: string;
  projectId: string;
  kind: DocumentKind;
  title: string;
  notes: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  /**
   * How it can be shown in the app, or null for a format nothing renders — decided by the
   * API, so the UI never offers a View that would open a blank frame.
   *
   * The first three are the file's own bytes, served inline by `/documents/:id/view`. The
   * last three are Office files, which no browser renders: those come from
   * `/documents/:id/preview`, already read into data on the server.
   */
  viewable: 'pdf' | 'image' | 'text' | 'sheet' | 'doc' | 'slides' | null;
  checksum: string;
  signed: boolean;
  signedOn: string | null;
  uploadedBy: { id: string; name: string } | null;
  createdAt: string;
  /** Decided by the API, so the UI never offers a delete it would refuse. */
  canDelete: boolean;
  /**
   * The task it is filed against, or null when it belongs to the project at large.
   *
   * One document, two places to find it: a file attached to a task appears in the project's
   * list as well, and this is how that list says where it came from.
   */
  workItem: { id: string; key: string; title: string } | null;
}

/** One piece of a Word document, as the API read it. Text only — never markup from the file. */
export type DocumentBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullet'; text: string }
  | { type: 'table'; rows: string[][] };

/**
 * A Word, Excel or PowerPoint file read into data, for a preview the app draws itself.
 *
 * The `more…` counts are how much was left out. They are carried so the screen can say it
 * rather than stop quietly and leave somebody believing the file ends there.
 */
export type DocumentPreview =
  | {
      kind: 'sheet';
      sheets: { name: string; rows: string[][]; moreRows: number; moreColumns: number }[];
      moreSheets: number;
    }
  | { kind: 'doc'; blocks: DocumentBlock[]; moreBlocks: number }
  | { kind: 'slides'; slides: { number: number; lines: string[] }[]; moreSlides: number };

/* ---------- service management: incidents, problems, changes ---------- */

export type IncidentStatus = 'open' | 'investigating' | 'waiting' | 'resolved' | 'closed';
export type ProblemStatus =
  | 'identified'
  | 'investigating'
  | 'known_error'
  | 'fix_in_progress'
  | 'resolved'
  | 'closed';
export type ChangeType = 'standard' | 'normal' | 'emergency';
export type ChangeStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'implemented' | 'closed' | 'rolled_back';
export type RiskLevel = 'low' | 'medium' | 'high';

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  waiting: 'Waiting on someone',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const PROBLEM_STATUS_LABELS: Record<ProblemStatus, string> = {
  identified: 'Identified',
  investigating: 'Investigating',
  known_error: 'Known error',
  fix_in_progress: 'Fix in progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const CHANGE_STATUS_LABELS: Record<ChangeStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  implemented: 'In',
  closed: 'Closed',
  rolled_back: 'Rolled back',
};

export interface Incident {
  id: string;
  reference: string;
  title: string;
  status: IncidentStatus;
  priority: Priority;
  severity: Severity;
  reportedAt: string;
  respondedAt: string | null;
  resolvedAt: string | null;
  reporterEmail: string | null;
  freshdeskTicketId: string | null;
  assignee: { id: string; name: string } | null;
  project: { id: string; name: string; code: string } | null;
  /** The cause, carrying the workaround — what support tells the next caller. */
  problem: {
    id: string;
    reference: string;
    title: string;
    status: ProblemStatus;
    workaround: string;
  } | null;
  /** Decided by the API: an `own` grant may change only what they reported. */
  editable: boolean;
  description?: string;
  resolution?: string;
}

export interface IncidentList {
  company: { id: string; name: string; code: string };
  capabilities: Record<string, Grant>;
  incidents: Incident[];
  counts: Partial<Record<IncidentStatus, number>>;
  total: number;
  projects: Array<{ id: string; name: string; code: string }>;
}

export interface Problem {
  id: string;
  reference: string;
  title: string;
  status: ProblemStatus;
  priority: Priority;
  owner: { id: string; name: string } | null;
  project: { id: string; name: string; code: string } | null;
  /** How much it is costing — the argument for prioritising it. */
  incidentCount: number;
  openIncidentCount: number;
  workaround: string;
  hasWorkaround: boolean;
  fix: { id: string; key: string; title: string; done: boolean } | null;
  resolvedAt: string | null;
  createdAt: string;
  description?: string;
  rootCause?: string;
  resolution?: string;
  /** Only on the detail view: the incidents this problem is said to explain. */
  incidents?: Array<{
    id: string;
    reference: string;
    title: string;
    status: IncidentStatus;
    priority: Priority;
    reportedAt: string;
    resolvedAt: string | null;
    project: { id: string; name: string; code: string } | null;
  }>;
}

export interface ProblemList {
  company: { id: string; name: string; code: string };
  capabilities: Record<string, Grant>;
  problems: Problem[];
  unexplainedCount: number;
  projects: Array<{ id: string; name: string; code: string }>;
}

export interface UnexplainedIncident {
  id: string;
  reference: string;
  title: string;
  status: IncidentStatus;
  priority: Priority;
  reportedAt: string;
  project: { id: string; name: string; code: string } | null;
}

export interface Change {
  id: string;
  reference: string;
  title: string;
  type: ChangeType;
  status: ChangeStatus;
  risk: RiskLevel;
  requestedBy: { id: string; name: string } | null;
  approvedBy: { id: string; name: string } | null;
  approvedAt: string | null;
  rejectionReason: string;
  merge: { iid: number; sourceBranch: string; targetBranch: string; mergedAt: string | null } | null;
  workItem: { id: string; key: string; title: string } | null;
  problem: { id: string; reference: string; title: string } | null;
  plannedFor: string | null;
  implementedAt: string | null;
  hasRollbackPlan: boolean;
  /** Whether GitLab confirmed the merge when this was marked in, or it is somebody's word. */
  mergeVerified: boolean;
  /** Why somebody recorded it anyway when GitLab could not confirm. Empty otherwise. */
  unverifiedReason: string;
  createdAt: string;
  /** Decided by the API — your own change is never yours to approve. */
  canApprove: boolean;
  canSubmit: boolean;
  canImplement: boolean;
  /** Why the approve action is missing, when the reason is that you raised it. */
  approvalBlockedReason: string | null;
  description?: string;
  rollbackPlan?: string;
  testEvidence?: string;
}

export interface ChangeList {
  project: { id: string; name: string; code: string; companyId: string };
  capabilities: Record<string, Grant>;
  changes: Change[];
  awaitingApproval: number;
}

/**
 * What GitLab says about a change's merge right now. `merged` is three-valued on purpose:
 * true, false, and null for "could not be asked" — which must never read as either.
 */
export type MergeOutcome = 'merged' | 'not_merged' | 'unreachable' | 'no_repository' | 'no_merge_request';

export interface MergeState {
  /** Why we know, or do not know. `unreachable` is the one that blocks. */
  outcome: MergeOutcome;
  checked: boolean;
  reachable: boolean;
  merged: boolean | null;
  state?: string;
  title?: string;
  webUrl?: string;
  reason: string | null;
}

/** What the login call returns when the password was right but a second step is owed. */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeId: string;
  method: 'email' | 'app';
  /** A masked address for the email method; null for the app, which posts nothing. */
  sentTo: string | null;
  digits: number;
  expiresInMinutes: number;
}

/** The one-time enrolment payload for an authenticator. Shown once, then it is the phone's. */
export interface AuthenticatorEnrolment {
  secret: string;
  uri: string;
  /** An SVG QR code for the same otpauth URI — a convenience, not a second exposure. */
  qr: string;
  digits: number;
}

/* ---------- rate cards ---------- */

/**
 * What a role costs per hour, and when.
 *
 * Versioned by date rather than edited in place: a cost is always worked out against the
 * rate that applied when the work happened, so raising a price must not silently re-rate
 * every finished project.
 */
export interface RateCardRow {
  id: string;
  companyId: string;
  role: CompanyRole;
  /** A numeric column over the wire, so a string — never a float. */
  hourlyRate: string;
  currency: string;
  effectiveFrom: string;
  /** Null means open-ended, and setting a later card closes it the day before. */
  effectiveTo: string | null;
}

/* ---------- baselines ---------- */

/**
 * The plan as somebody agreed it. `current` is the newest, which is what the chart's ghost
 * bars and the two "Agreed" columns are drawn from — decided by the API rather than a stored
 * flag, so removing one leaves nothing pointing at nothing.
 */
export interface PlanBaseline {
  id: string;
  name: string;
  note: string;
  takenAt: string;
  takenBy: string | null;
  tasks: number;
  current: boolean;
}

/* ---------- the activity log ---------- */

/** Who the feed was filtered for. Decided by the API from capabilities, never asked for. */
export type ActivityAudience = 'client' | 'staff' | 'admin';

export interface ActivityEvent {
  id: string;
  at: string;
  action: string;
  /** The person's name. The address is administrators-only, and only on the audit page. */
  who: string;
  entityType: string;
  entityId: string | null;
  entityLabel: string;
  companyId: string | null;
  projectId: string | null;
  /** Administrators only — free-form JSON from ninety call sites, so it never travels wider. */
  details?: Record<string, unknown> | null;
  actorEmail?: string;
  ipAddress?: string;
}

export interface ProjectActivity {
  audience: ActivityAudience;
  events: ActivityEvent[];
}

export interface AuditPage {
  events: ActivityEvent[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface AuditVocabulary {
  actions: string[];
  /** The first segment of each action — `work_item`, `risk`, `auth` — for the filter. */
  groups: string[];
}

/**
 * `work_item.plan_reorder` as somebody would say it.
 *
 * The action is a machine name and reads like one. Rather than a ninety-entry lookup that
 * goes stale the moment somebody adds an event, the group is mapped and the rest is
 * de-underscored — so an unknown action still reads as English rather than as a key.
 */
const ACTIVITY_GROUPS: Record<string, string> = {
  work_item: 'Work',
  project: 'Project',
  company: 'Company',
  person: 'Account',
  auth: 'Sign-in',
  risk: 'Risk',
  release: 'Release',
  change: 'Change',
  incident: 'Incident',
  problem: 'Problem',
  sprint: 'Sprint',
  time: 'Time',
  document: 'Document',
  comment: 'Comment',
  gitlab: 'GitLab',
  rate_card: 'Rate',
  task_group: 'Section',
  category: 'Category',
};

export function activityGroupLabel(group: string): string {
  return ACTIVITY_GROUPS[group] ?? group.replace(/_/g, ' ');
}

export function activityActionLabel(action: string): string {
  const [group, ...rest] = action.split('.');
  const what = rest.join('.').replace(/_/g, ' ');
  return `${activityGroupLabel(group)} · ${what}`;
}

/* ---------- release reports: what went live ---------- */

export interface ReleaseSend {
  at: string;
  byId: string;
  byName: string;
  to: string[];
}

export interface ReleaseSummary {
  id: string;
  title: string;
  releasedOn: string;
  createdBy: string | null;
  companies: string[];
  projects: number;
  tasks: number;
  lastSent: ReleaseSend | null;
}

export interface ReleaseLine {
  id: string;
  projectId: string | null;
  workItemId: string | null;
  companyName: string;
  projectName: string;
  workItemKey: string;
  workItemTitle: string;
  note: string;
}

export interface ReleaseDetail {
  id: string;
  title: string;
  releasedOn: string;
  summary: string;
  createdBy: string | null;
  createdById: string | null;
  createdAt: string;
  sends: ReleaseSend[];
  items: ReleaseLine[];
  /** Lines on projects this reader cannot see — counted so the report does not look short. */
  hiddenLines: number;
  mayDelete: boolean;
}

export interface ReleaseCandidates {
  from: string;
  to: string;
  projects: Array<{
    company: { id: string; name: string };
    project: { id: string; name: string; code: string; status: ProjectStatus };
    items: Array<{ id: string; key: string; title: string; finishedAt: string | null }>;
  }>;
}

/* ---------- the company overview: management's view of one company ---------- */

export type Health = 'red' | 'amber' | 'green' | 'done' | 'none';
export type TaskRisk = 'late' | 'blocked' | 'due_soon';

export const HEALTH_LABELS: Record<Health, string> = {
  red: 'Off track',
  amber: 'At risk',
  green: 'On track',
  done: 'Done',
  none: 'No tasks yet',
};

export const TASK_RISK_LABELS: Record<TaskRisk, string> = {
  late: 'Overdue',
  blocked: 'Blocked',
  due_soon: 'Due soon, not started',
};

export interface OverviewProject {
  id: string;
  name: string;
  code: string;
  status: ProjectStatus;
  priority: Priority;
  design: boolean;
  targetDate: string | null;
  projectedFinish: string | null;
  forecast: 'on_track' | 'at_risk' | 'late' | 'done' | 'unknown';
  percentComplete: number;
  /** Budget, spent and planned in rand — only for whoever may see money. */
  money?: { budget: number | null; spent: number | null; planned: number | null };
  health: Health;
  reasons: string[];
  counts: { total: number; open: number; done: number; overdue: number; blocked: number };
  span: { start: string | null; end: string | null };
}

export interface OverviewTask {
  id: string;
  key: string;
  title: string;
  type: WorkItemType;
  priority: Priority;
  projectId: string;
  stage: string | null;
  percent: number | null;
  status: { label: string; color: string | null; category: string | null } | null;
  assignees: string;
  startDate: string | null;
  dueDate: string | null;
  done: boolean;
  finishedOn: string | null;
  blocked: boolean;
  risk: TaskRisk | null;
}

export interface CompanyOverview {
  company: { id: string; name: string; code: string; kind: CompanyKind };
  generatedAt: string;
  today: string;
  withoutDesign: boolean;
  totals: {
    projects: number;
    tasks: number;
    open: number;
    done: number;
    overdue: number;
    blocked: number;
    dueSoon: number;
    unassigned: number;
    percentComplete: number;
    health: Record<Health, number>;
  };
  projects: OverviewProject[];
  tasks: OverviewTask[];
  attention: OverviewTask[];
  dashboard: OverviewDashboard;
}

/** The dashboard over every project's plan — see backend `reporting/company-plan.ts`. */
export interface OverviewDashboard {
  window: { from: string | null; to: string | null };
  /** Where the plan says the work should be by today, where it is, and the gap. Percent. */
  time: { planPct: number; workPct: number; behindBy: number };
  tasks: { total: number; notStarted: number; inProgress: number; finished: number; overdue: number };
  /** Null when there is nothing to add up — or the reader may not see money. */
  cost: { spent: number | null; planned: number | null };
  workload: Array<{ name: string; left: number; hours: number; overdue: number; done: number }>;
  byProject: Array<{ id: string; name: string; percent: number }>;
  /** The budgets set on the projects shown, added up. Null when none is, or money is not theirs. */
  budget: { total: number; projects: number } | null;
}

/* ---------- the company risk register ---------- */

export type RiskSeverity = 'high' | 'medium' | 'low';
export type RiskStatus = 'open' | 'mitigating' | 'accepted' | 'closed';
export type RiskCategory = 'delivery' | 'technical' | 'financial' | 'commercial' | 'people' | 'security' | 'compliance' | 'other';

export const RISK_CATEGORY_LABELS: Record<RiskCategory, string> = {
  delivery: 'Delivery',
  technical: 'Technical',
  financial: 'Financial',
  commercial: 'Commercial',
  people: 'People',
  security: 'Security',
  compliance: 'Compliance',
  other: 'Other',
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  open: 'Open',
  mitigating: 'Being mitigated',
  accepted: 'Accepted',
  closed: 'Closed',
};

export interface RiskRow {
  id: string;
  reference: string;
  title: string;
  description: string;
  category: RiskCategory;
  project: { id: string; name: string } | null;
  likelihood: number;
  impact: number;
  score: number;
  severity: RiskSeverity;
  status: RiskStatus;
  owner: { id: string; name: string } | null;
  reviewOn: string | null;
  outcome: string;
  raisedBy: string | null;
  raisedAt: string;
  closedAt: string | null;
  closedBy: string | null;
  mitigations: Array<{ id: string; action: string; done: boolean; owner: { id: string; name: string } | null; dueOn: string | null }>;
  documents: Array<{
    id: string;
    title: string;
    originalName: string;
    mimeType: string;
    /** How it is shown in the viewer, as a project document's; null means download only. */
    viewable: ProjectDocument['viewable'];
    sizeBytes: number;
    uploadedBy: string | null;
    uploadedAt: string;
  }>;
}

export interface RiskRegisterData {
  company: { id: string; name: string; code: string } | null;
  summary: { open: number; high: number; medium: number; low: number; unowned: number; reviewDue: number; closed: number };
  risks: RiskRow[];
  owners: Array<{ id: string; name: string }>;
  projects: Array<{ id: string; name: string }>;
}
